import React, { useEffect, useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from '../../components/layouts/AdminLayout';
import StatusBadge from '../../components/shared/StatusBadge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { 
  Search, 
  Filter, 
  Truck, 
  ArrowRight,
  Plus,
  Map,
  TrendingUp
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AcceptedShipments = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [waybills, setWaybills] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'all');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 15;

  const acceptedStatuses = ['acknowledged', 'accepted', 'confirmed', 'dispatch_scheduled', 'collected', 'in_transit', 'at_customs', 'out_for_delivery', 'delayed', 'delivered'];

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch stats
        const statsRes = await axios.get(`${API_URL}/api/dashboard/admin-stats`);
        setStats(statsRes.data);

        // Fetch waybills
        let url = `${API_URL}/api/waybills?page=${page}&limit=${limit}`;
        if (search) url += `&search=${encodeURIComponent(search)}`;
        
        const activeStatuses = acceptedStatuses.filter(s => s !== 'delivered');

        if (statusFilter === 'active') {
          url += `&status=${activeStatuses.join(',')}`;
        } else if (statusFilter !== 'all') {
          url += `&status=${statusFilter}`;
        }

        const response = await axios.get(url);
        const filtered = statusFilter === 'all' 
          ? response.data.waybills.filter(w => acceptedStatuses.includes(w.status))
          : statusFilter === 'active'
            ? response.data.waybills.filter(w => activeStatuses.includes(w.status))
            : response.data.waybills;
        setWaybills(filtered);
        setTotal(filtered.length);
      } catch (err) {
        console.error('Error fetching data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [page, search, statusFilter]);

  const updateStatus = async (waybillId, newStatus) => {
    try {
      await axios.put(`${API_URL}/api/waybills/${waybillId}/status`, {
        status: newStatus,
        location: 'Updated via dashboard',
        notes: `Status changed to ${newStatus}`,
        customer_visible: true
      });
      toast.success('Status updated');
      // Refresh
      const response = await axios.get(`${API_URL}/api/waybills?page=${page}&limit=${limit}`);
      const filtered = response.data.waybills.filter(w => acceptedStatuses.includes(w.status));
      setWaybills(filtered);
      setTotal(filtered.length);
      // Refresh stats too
      const statsRes = await axios.get(`${API_URL}/api/dashboard/admin-stats`);
      setStats(statsRes.data);
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return '-';
    return `R ${Number(amount).toLocaleString('en-ZA', { minimumFractionDigits: 2 })}`;
  };

  const statuses = [
    { value: 'all', label: 'All Waybills' },
    { value: 'active', label: 'Active Waybills' },
    { value: 'acknowledged', label: 'Accepted' },
    { value: 'collected', label: 'Collected' },
    { value: 'in_transit', label: 'In Transit' },
    { value: 'out_for_delivery', label: 'Out for Delivery' },
    { value: 'delivered', label: 'Delivered' },
    { value: 'delayed', label: 'Delayed' },
  ];

  const totalPages = Math.ceil(total / limit);

  return (
    <AdminLayout>
      <div className="animate-fade-in" data-testid="accepted-shipments-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="page-title">Waybills</h1>
            <p className="text-slate-600 mt-1">Manage and track all waybills and shipments.</p>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" className="border-slate-200">
              <Filter className="h-4 w-4 mr-2" />
              Filter View
            </Button>
            <Button className="btn-primary">
              <Plus className="h-4 w-4 mr-2" />
              New Dispatch
            </Button>
          </div>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <Card className="card-elevated">
            <CardContent className="p-4">
              <p className="text-sm text-slate-500">In Transit</p>
              <p className="text-2xl font-bold text-slate-900">{stats?.in_transit || 0}</p>
            </CardContent>
          </Card>
          <Card className="card-elevated">
            <CardContent className="p-4">
              <p className="text-sm text-slate-500">Out for Delivery</p>
              <p className="text-2xl font-bold text-slate-900">{stats?.out_for_delivery || 0}</p>
            </CardContent>
          </Card>
          <Card className="card-elevated">
            <CardContent className="p-4">
              <p className="text-sm text-slate-500">Delayed</p>
              <p className="text-2xl font-bold text-red-600">{stats?.delayed || 0}</p>
            </CardContent>
          </Card>
          <Card className="card-elevated">
            <CardContent className="p-4">
              <p className="text-sm text-slate-500">Delivered Today</p>
              <p className="text-2xl font-bold text-emerald-600">{stats?.delivered_today || 0}</p>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="card-elevated mb-6">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                  placeholder="Search waybills..."
                  className="pl-10"
                  data-testid="shipments-search"
                />
              </div>
              <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
                <SelectTrigger className="w-48" data-testid="shipments-filter">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  {statuses.map(s => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card className="card-elevated">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-700"></div>
              </div>
            ) : waybills.length > 0 ? (
              <>
                <div className="overflow-x-auto">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Waybill ID</th>
                        <th>Client</th>
                        <th>Route</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {waybills.map((wb) => (
                        <tr 
                          key={wb.waybill_id} 
                          className="cursor-pointer hover:bg-slate-50 transition-colors"
                          onClick={() => navigate(`/admin/shipment/${wb.waybill_id}`, { state: { from: '/admin/accepted-shipments' } })}
                          data-testid={`waybill-row-${wb.reference_number}`}
                        >
                          <td>
                            <span className="text-teal-700 font-medium">#{wb.reference_number}</span>
                          </td>
                          <td>
                            <div className="flex items-center space-x-2">
                              <div className="h-8 w-8 rounded-full bg-slate-100 flex items-center justify-center text-xs font-semibold text-slate-600">
                                {wb.customer?.company_name?.substring(0, 2).toUpperCase() || 'CL'}
                              </div>
                              <div>
                                <p className="font-medium text-slate-900">{wb.customer?.company_name || wb.sender_company}</p>
                                <p className="text-xs text-slate-500">{wb.customer?.account_code || ''}</p>
                              </div>
                            </div>
                          </td>
                          <td>
                            <div className="flex items-center">
                              <span>{wb.origin_city}</span>
                              <span className="text-slate-400 mx-2">→</span>
                              <span>{wb.destination_city}</span>
                            </div>
                          </td>
                          <td><StatusBadge status={wb.status} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between px-6 py-4 border-t border-slate-100">
                  <p className="text-sm text-slate-500">
                    Showing {waybills.length} of {total} active waybills
                  </p>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage(p => p - 1)}>
                      Previous
                    </Button>
                    {[1, 2, 3].map(n => (
                      <Button
                        key={n}
                        variant={page === n ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setPage(n)}
                        className={page === n ? 'bg-teal-700' : ''}
                      >
                        {n}
                      </Button>
                    ))}
                    <Button variant="outline" size="sm" onClick={() => setPage(p => p + 1)}>
                      Next
                    </Button>
                  </div>
                </div>
              </>
            ) : (
              <div className="text-center py-12 text-slate-500">
                No active shipments found
              </div>
            )}
          </CardContent>
        </Card>

        {/* Map Button */}
        <div className="fixed bottom-8 left-1/2 -translate-x-1/2">
          <Button className="bg-slate-900 hover:bg-slate-800 text-white shadow-lg px-6 py-3 rounded-full">
            <Map className="h-4 w-4 mr-2" />
            Open Live Network Map
          </Button>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AcceptedShipments;
